import { Injectable } from '@angular/core';
import { Auth, signInWithEmailAndPassword } from '@angular/fire/auth';

import * as auth from 'firebase/auth';
import { User } from '@services/core-services/models';
import { TokenService } from '@services/auth-services/token.service';
import { AuthErrorsTypes } from '@services/auth-services/models';
import { UrlService } from '@services/auth-services/url.service';
import { AuthErrorsService } from '@services/auth-services/auth-errors.service';
import { BehaviorSubject, from, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private user = new BehaviorSubject<User | undefined>(undefined);

  constructor(
    private readonly auth: Auth,
    private readonly tokenService: TokenService,
    private readonly urlService: UrlService,
    private readonly authErrors: AuthErrorsService
  ) {}

  /**
   * Gets currently logged user from the state
   */
  getCurrentUser() {
    return this.user.getValue();
  }

  /**
   * Update user's state
   * @param user
   */
  setCurrentUser(user: User | undefined) {
    this.user.next(user);
    return user;
  }

  /**
   * Gets current user observable.
   */
  getUser() {
    return this.user.asObservable();
  }

  /**
   * Gets the currently authenticated user and verifies that it's data are written into cookies
   */
  getAuth(): Observable<User> {
    return from(
      this.auth
        .authStateReady()
        .then(() => this.auth.currentUser)
        .then(user => this.verifyAuth(user))
    );
  }

  /**
   * Method used to log in user with email and password.
   *
   * @param email
   * @param password
   * @param tenantId
   * @returns user
   */
  async loginWithEmailAndPassword(email: string, password: string, tenantId: string) {
    try {
      this.auth.tenantId = tenantId;
      const { user } = await signInWithEmailAndPassword(this.auth, email, password);

      const { emailVerified } = user;

      if (!emailVerified) {
        throw this.authErrors.getError(AuthErrorsTypes.EMAIL_NOT_VERIFIED);
      }

      const result = await this.verifyAuth(user);
      this.user.next(result);
      return result;
    } catch (er: any) {
      const code = er.code;
      throw this.authErrors.getError(code);
    }
  }

  /**
   * Sign in with Google
   */
  async loginWithGoogle(tenantId: string | null) {
    return await this.signInWithPopup(new auth.GoogleAuthProvider(), tenantId);
  }

  /**
   * Sign in with GitHub
   */
  async loginWithGithub(tenantId: string | null) {
    return await this.signInWithPopup(new auth.GithubAuthProvider(), tenantId);
  }

  /**
   * Sign in with Microsoft
   */
  async loginWithMicrosoft(tenantId: string | null) {
    return await this.signInWithPopup(new auth.OAuthProvider('microsoft.com'), tenantId);
  }

  /**
   * Sign in with Saml
   */
  async loginWithSaml(tenantId: string | null) {
    return await this.signInWithPopup(new auth.SAMLAuthProvider('saml.saml.tarkai'), tenantId);
  }

  /**
   * Auth logic to run auth providers.
   * @param provider
   * @param tenantId
   */
  async signInWithPopup(provider: any, tenantId: string | null) {
    try {
      if (tenantId) {
        this.auth.tenantId = tenantId;
      }

      const { user } = await auth.signInWithPopup(this.auth, provider);
      await this.verifyAuth(user);
      this.user.next(user);
      return user;
    } catch (er: any) {
      throw this.authErrors.getError(er.code);
    }
  }

  /**
   * Initiates the reset password flow.
   * @param passwordResetEmail
   * @param tenantId
   */
  async initiateForgetPassword(passwordResetEmail: string, tenantId: string) {
    const { subdomain, domain, protocol } = this.urlService.getUrlSettings();
    this.auth.tenantId = tenantId;
    const actionCodeSettings = {
      url: `${protocol}//${domain}?subdomain=${subdomain}`,
      handleCodeInApp: false,
    };
    try {
      return await auth.sendPasswordResetEmail(this.auth, passwordResetEmail, actionCodeSettings);
    } catch (er: any) {
      throw this.authErrors.getError(er.code);
    }
  }

  /**
   * Reset user password.
   * @param actionCode
   * @param tenantId
   * @param newPassword
   */
  resetPassword(actionCode: string, tenantId: string, newPassword: string) {
    this.auth.tenantId = tenantId;
    return auth.confirmPasswordReset(this.auth, actionCode, newPassword);
  }

  /**
   * Logout user
   */
  logout() {
    this.tokenService.clear();
    this.user.next(undefined);
    return auth.signOut(this.auth);
  }

  /**
   * Handles the email verification
   * @param actionCode
   */
  handleVerifyEmail(actionCode: any) {
    console.log(actionCode);
    return auth.applyActionCode(this.auth, actionCode);
  }

  /**
   * Handles the reset password.
   * @param actionCode
   */
  handleResetPassword(actionCode: string) {
    return auth.verifyPasswordResetCode(this.auth, actionCode);
  }

  /**
   * Creates email credentials
   * @param email
   * @param password
   */
  createEmailCredential(email: string, password: string) {
    return auth.EmailAuthProvider.credential(email, password);
  }

  /**
   * Links user with email address.
   * @param user
   * @param cred
   */
  linkEmailAccount(user: any, cred: any) {
    return auth.linkWithCredential(user, cred);
  }

  /**
   * Tries to re-authenticate the user
   * @param current_user
   * @param cred
   */
  reAuthenticate(current_user: any, cred: any) {
    return auth.reauthenticateWithCredential(current_user, cred);
  }

  /**
   * Updates user password
   * @param user
   * @param new_password
   */
  updatePassword(user: any, new_password: string) {
    return auth.updatePassword(user, new_password);
  }

  /**
   * Verifies user with token id, and makes sure that roles are in place.
   * It also saves tokens locally.
   * @param user
   * @param force
   * @private
   */
  private async verifyAuth(user: any, force = true) {
    const { claims, token } = await auth.getIdTokenResult(user, force);
    const roles: any[] | undefined = claims['roles'] as string[];
    if (!roles) {
      throw this.authErrors.getError(AuthErrorsTypes.MISSING_ROLES);
    }
    const default_store_id = this.setCurrentStore(roles);
    this.tokenService.saveAuthTokens(token, user.refreshToken);
    return { ...user, default_store_id };
  }

  /**
   * Set current store
   * @param roles
   * @private
   */
  private setCurrentStore(roles: any[]) {
    const role = roles.find((s: any) => s.is_default_store) || roles[0];

    const store_id = role?.store_id;
    if (store_id) {
      this.tokenService.setCurrentStoreId(role?.store_id);
    }
    return store_id;
  }
}
